import {
  type ChangeEvent,
  type FC,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  useId,
} from "react";
import { twMerge } from "tailwind-merge";

import { CheckIcon } from "mds/icons";
import { BUTTON_EVENT_CATEGORY, ComponentEventProperties, trackEvent } from "utils/amplitude";

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> &
  ComponentEventProperties & {
    children?: ReactNode;
    errorMessage?: string;
    ref?: Ref<HTMLInputElement>;
  };

const INDICATOR_CLASSES = twMerge(
  "pointer-events-none flex size-4 items-center justify-center rounded-sm border border-black-tint-70 bg-transparent supports-squircle:rounded-[4px] supports-squircle:corner-squircle",
  "peer-hover:border-black-tint-55",
  "peer-checked:border-transparent peer-checked:bg-blue-shade-20 peer-checked:[&_svg]:opacity-100",
  "peer-checked:peer-hover:border-transparent peer-checked:peer-hover:bg-blue",
  "peer-disabled:cursor-not-allowed peer-disabled:border-black-tint-86 peer-disabled:bg-transparent",
  "peer-disabled:peer-hover:border-black-tint-86 peer-disabled:peer-hover:bg-transparent",
  "peer-checked:peer-disabled:border-black-tint-70 peer-checked:peer-disabled:bg-black-tint-70 peer-checked:peer-disabled:peer-hover:border-black-tint-70 peer-checked:peer-disabled:peer-hover:bg-black-tint-70",
  "peer-focus-visible:ring-2 peer-focus-visible:ring-blue peer-focus-visible:ring-offset-1",
);

export const Checkbox: FC<CheckboxProps> = ({
  children,
  className,
  ref,
  eventName,
  eventProperties,
  errorMessage,
  onChange,
  id,
  "aria-label": ariaLabel,
  ...inputProps
}) => {
  const generatedId = useId();
  const errorId = `${id ?? generatedId}-error`;

  if (process.env.NODE_ENV !== "production" && !children && !ariaLabel) {
    console.error("Checkbox requires either children or an aria-label");
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (eventName) {
      trackEvent(eventName, {
        ...BUTTON_EVENT_CATEGORY,
        isEnabled: e.target.checked,
        ...eventProperties,
      });
    }
    onChange?.(e);
  };

  const control = (
    <label
      className={twMerge(
        "body-s flex items-center gap-2 text-black-tint-20 has-[:disabled]:cursor-not-allowed has-[:disabled]:text-black-tint-55",
        className,
      )}
    >
      <span className="relative size-4 shrink-0">
        <input
          {...inputProps}
          aria-describedby={errorMessage ? errorId : undefined}
          aria-invalid={errorMessage ? true : undefined}
          aria-label={ariaLabel}
          className="peer absolute inset-0 z-10 m-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          id={id}
          ref={ref}
          type="checkbox"
          onChange={handleChange}
        />
        <span className={INDICATOR_CLASSES} aria-hidden>
          <CheckIcon className="text-white opacity-0" size="s" />
        </span>
      </span>
      {children}
    </label>
  );

  if (!errorMessage) {
    return control;
  }

  return (
    <div>
      {control}
      <div className="body-xs mt-1 min-w-0 break-words text-red" id={errorId}>
        {errorMessage}
      </div>
    </div>
  );
};

Checkbox.displayName = "Checkbox";
